import { Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { WhitelabelService } from './domain/whitelabel.service';
import { Router, NavigationError, ActivatedRoute, NavigationStart } from '@angular/router';
import { IConfirmationReq, LoginGuardService } from './api/login-guard.service';
import { LangService } from './core/lang.service';
import { Subscription } from 'rxjs';
import { ChatService } from './chat/chat.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnDestroy {
  @ViewChild('confirmationMessage', { static: false }) confirmationMessage: ElementRef<HTMLElement>;
  @ViewChild('loginOverlay', { static: false }) loginOverlay: ElementRef<HTMLElement>;
  @ViewChild('deviceNetworkMessage', { static: false }) deviceNetworkMessage: ElementRef<HTMLElement>;
  @ViewChild('apiNetworkMessage', { static: false }) apiNetworkMessage: ElementRef<HTMLElement>;
  @ViewChild('infoOverlayHeading', { static: false }) infoOverlayHeading: ElementRef<HTMLElement>;

  subscription = new Subscription();
  private tabindexMap: Map<HTMLElement, string | null> = new Map();
  private focusedConfirmation: IConfirmationReq;
  private previouslyFocusedElement: HTMLElement;
  private popupFocusFrames: Map<string, number> = new Map();
  private popupPreviouslyFocusedElements: Map<string, HTMLElement> = new Map();

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private loginGuard: LoginGuardService,
    public lang: LangService,
    public whiteLabel: WhitelabelService,
    public chatService: ChatService
  ) {
    this.applySiteLock();
    this.setupRouterDefault();
    this.setupConnectionOverlaySubs();
  }

  ngOnDestroy() {
    this.cancelConfirmationMessageFocus();
    this.popupFocusFrames.forEach(frame => window.cancelAnimationFrame(frame));
    this.popupFocusFrames.clear();
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  title = 'app';
  isCertifiedRelease: boolean;
  isCertifiedReleaseInvalidated: boolean;
  isShowingDevNetFailOverlay: boolean;
  isShowingApiNetFailOverlay: boolean;

  confirmationProceedCaption: string;
  confirmationCancelCaption: string;

  currentConfirmation: IConfirmationReq;
  isShowingInfoOverlay: boolean;

  getLoginReq = () => this.loginGuard.getLoginReq();
  getDevNetFail = () => this.loginGuard.getDevNetFail();
  getApiNetFail = () => this.loginGuard.getApiNetFail();
  getSupportReq = () => this.loginGuard.getSupportReq();
  getAutoLogout = () => this.loginGuard.getAutoLogout();
  getAutoLogoutWarning = () => this.loginGuard.getAutoLogoutWarning();
  refreshLogin = () => this.loginGuard.refreshLogin();

  setupConnectionOverlaySubs() {
    this.subscription.add(this.getLoginReq().subscribe(v => {
      this.updatePopupFocus('login', !!v, () => {
        const overlay = this.loginOverlay && this.loginOverlay.nativeElement;
        return overlay && overlay.querySelector('#username') as HTMLElement;
      });
    }));
    this.subscription.add(this.getDevNetFail().subscribe(v => {
      this.isShowingDevNetFailOverlay = v;
      this.updatePopupFocus('device-network', v, () => this.deviceNetworkMessage && this.deviceNetworkMessage.nativeElement);
    }));
    this.subscription.add(this.getApiNetFail().subscribe(v => {
      this.isShowingApiNetFailOverlay = v;
      this.updatePopupFocus('api-network', v, () => this.apiNetworkMessage && this.apiNetworkMessage.nativeElement);
    }));
    this.subscription.add(this.loginGuard.getInfoOverlay().subscribe(v => {
      this.isShowingInfoOverlay = v;
      this.updatePopupFocus('instructions', v, () => this.infoOverlayHeading && this.infoOverlayHeading.nativeElement);
    }));
    this.subscription.add(
      this.loginGuard.getConfirmationReq().subscribe(v => {
        if (v && !this.currentConfirmation) {
          this.previouslyFocusedElement = document.activeElement as HTMLElement;
        }
        this.trapFocus(v);
        this.currentConfirmation = <any>v;
        if (v) {
          this.confirmationProceedCaption = v.btnProceedCaption || 'OK';
          this.confirmationCancelCaption = v.btnCancelCaption || this.lang.tra('btn_cancel');
          this.scheduleConfirmationMessageFocus(v);
        } else {
          this.focusedConfirmation = null;
          this.cancelConfirmationMessageFocus();
          this.restoreFocusAfterConfirmation();
        }
      })
    );
  }

  private updatePopupFocus(key: string, isShowing: boolean, getFocusTarget: () => HTMLElement) {
    if (isShowing) {
      if (!this.popupPreviouslyFocusedElements.has(key)) {
        const activeElement = document.activeElement as HTMLElement;
        if (activeElement && activeElement !== document.body) {
          this.popupPreviouslyFocusedElements.set(key, activeElement);
        }
      }
      this.schedulePopupFocusAfterRender(key, () => {
        const focusTarget = getFocusTarget();
        if (focusTarget && focusTarget.isConnected) {
          focusTarget.focus();
        }
      });
      return;
    }

    this.cancelPopupFocus(key);
    this.restorePopupFocus(key);
  }

  private schedulePopupFocusAfterRender(key: string, callback: () => void) {
    this.cancelPopupFocus(key);

    const firstFrame = window.requestAnimationFrame(() => {
      const secondFrame = window.requestAnimationFrame(() => {
        this.popupFocusFrames.delete(key);
        callback();
      });
      this.popupFocusFrames.set(key, secondFrame);
    });
    this.popupFocusFrames.set(key, firstFrame);
  }

  private cancelPopupFocus(key: string) {
    const frame = this.popupFocusFrames.get(key);
    if (frame !== undefined) {
      window.cancelAnimationFrame(frame);
      this.popupFocusFrames.delete(key);
    }
  }

  private restorePopupFocus(key: string) {
    const focusTarget = this.popupPreviouslyFocusedElements.get(key);
    this.popupPreviouslyFocusedElements.delete(key);
    if (focusTarget) {
      this.schedulePopupFocusAfterRender(key, () => {
        if (focusTarget.isConnected) {
          focusTarget.focus();
        }
      });
    }
  }

  private trapFocus(confirmation: any) {
    const dialogButtons: Element[] = Array.from(document.querySelectorAll('.confirmation-overlay button'));
    if (confirmation) {
      if (this.tabindexMap.size === 0) {
        document.querySelectorAll('button, input, select, textarea, .button').forEach(el => {
          const htmlElement = el as HTMLElement;
          this.tabindexMap.set(htmlElement, htmlElement.getAttribute('tabindex'));
          if (dialogButtons.indexOf(el) === -1) {
            htmlElement.setAttribute('tabindex', '-1');
          }
        });
      }
      return;
    }

    this.tabindexMap.forEach((value, key) => {
      if (value === null) {
        key.removeAttribute('tabindex');
      } else {
        key.setAttribute('tabindex', value);
      }
    });
    this.tabindexMap = new Map();
  }

  private scheduleConfirmationMessageFocus(confirmation: IConfirmationReq) {
    if (this.focusedConfirmation === confirmation) {
      return;
    }
    this.cancelConfirmationMessageFocus();
    this.schedulePopupFocusAfterRender('confirmation', () => {
      if (this.currentConfirmation !== confirmation) {
        return;
      }
      const focusTarget = this.confirmationMessage && this.confirmationMessage.nativeElement;
      if (focusTarget) {
        this.focusedConfirmation = confirmation;
        focusTarget.focus();
      }
    });
  }

  private cancelConfirmationMessageFocus() {
    this.cancelPopupFocus('confirmation');
  }

  private restoreFocusAfterConfirmation() {
    const focusTarget = this.previouslyFocusedElement;
    this.previouslyFocusedElement = null;
    if (focusTarget) {
      this.schedulePopupFocusAfterRender('confirmation', () => {
        if (document.body.contains(focusTarget)) {
          focusTarget.focus();
        }
      });
    }
  }

  applySiteLock() {
    const STORE_ADDR = 'site_lock';
    const currentTimestamp = new Date().valueOf();
    const prevTimestamp = window.localStorage.getItem(STORE_ADDR);
    if (prevTimestamp && currentTimestamp < parseFloat(prevTimestamp)) {
      this.isCertifiedRelease = true;
    }
    this.isCertifiedRelease = true; // comment this out before release for now...

    if (!this.isCertifiedRelease) {
      if (btoa((prompt('Password?') + '').trim().toLowerCase()) === 'dnI4MTI=') { // vr812
        this.isCertifiedRelease = true;
        const goodForSeconds = 30 * 60 * 1000;
        window.localStorage.setItem(STORE_ADDR, '' + (currentTimestamp + goodForSeconds));
      } else {
        this.isCertifiedReleaseInvalidated = true;
      }
    }
  }

  isAuthActive() {
    return this.loginGuard.isAuthActive();
  }

  closeModal() {
    this.currentConfirmation.close();
    this.loginGuard.confirmationReqDeactivate();
  }
  confirmModal() {
    this.currentConfirmation.confirm();
    this.loginGuard.confirmationReqDeactivate();
  }

  showCancel() {
    if (this.currentConfirmation) {
      return this.currentConfirmation.hideCancel;
    }
  }
  
  setCustomMaxWidth(){
    const defaultWidth = '30em';
    if(this.currentConfirmation && this.currentConfirmation.maxWidth){
      return this.currentConfirmation.maxWidth;
    }
    return defaultWidth;
  }

  dismissSupportPopup() {
    this.loginGuard.supportReqDeactivate();
  }

  dismissDeviceNetworkOverlay() {
    this.isShowingDevNetFailOverlay = false;
    this.updatePopupFocus('device-network', false, () => null);
  }

  dismissApiNetworkOverlay() {
    this.isShowingApiNetFailOverlay = false;
    this.updatePopupFocus('api-network', false, () => null);
  }

  gotoHomeScreen() {
    this.router.navigate([
      this.whiteLabel.getHomepageRoute()
    ]);
  }

  closeInfoOverlay() {
    this.loginGuard.toggleInfoOverlay();
  }

  setupRouterDefault() {
    this.route.params.subscribe(params => {
      // console.log(params, params)
    });
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart && event.url) {
        this.lang.updateCurrentUrl(event.url);
        const routePath = event.url.split('/');
        this.lang.setCurrentLanguage(routePath[1]);
      }
      if (event instanceof NavigationError) {
        console.warn('NavigationError', event);
        setTimeout(() => {
          this.gotoHomeScreen();
        }, 100);
      }
    });
  }

  getNextPicUrl() {
    return this.whiteLabel.getSiteText(`NEXT_PIC_URL_${this.lang.getCurrentLanguage().toUpperCase()}`, '');
  }

  getBackPicUrl() {
    return this.whiteLabel.getSiteText(`BACK_PIC_URL_${this.lang.getCurrentLanguage().toUpperCase()}`, '');
  }

  getTextPicUrl() {
    return this.whiteLabel.getSiteText(`TEXT_PIC_URL_${this.lang.getCurrentLanguage().toUpperCase()}`, '');
  }
}
